import styled from 'styled-components';
import { ReactComponent as DownstreamIcon } from '@elementary/common-ui/assets/stream-down.svg';
import { ReactComponent as UpstreamIcon } from '@elementary/common-ui/assets/stream-up.svg';
import { ReactComponent as StreamIcon } from '@elementary/common-ui/assets/stream.svg';
import { Button, Flex } from '@elementary/common-ui/components';
import { Tooltip } from '@mui/material';
import type { DagDepthBoundary, HopLimit } from '../types';
import HopSelector from './HopSelector';

const StreamButton = styled(Button)<{ $active: boolean }>`
  width: 44px;
  min-width: 0;
  background: ${({ theme, $active }) =>
    $active ? theme.palette.surface.inverse : theme.palette.surface.muted};

  :hover {
    background: ${({ theme, $active }) =>
      $active ? theme.palette.surface.inverse : theme.palette.surface.strong};
  }

  path {
    fill: ${({ theme, $active }) =>
      $active ? theme.palette.icon.inverse : theme.palette.icon.primary};
  }
`;

type Props = {
  hopLimit?: { downstream?: number; upstream?: number };
  onHopLimitChange: (val?: HopLimit) => void;
  dagDepthBoundary?: DagDepthBoundary;
};

const StreamSelector = ({
  hopLimit,
  onHopLimitChange,
  dagDepthBoundary,
}: Props) => {
  const upstreamValue = (
    hopLimit?.upstream === undefined
      ? dagDepthBoundary?.incomingDepth || 0
      : hopLimit.upstream
  ).toString();

  const downstreamValue = (
    hopLimit?.downstream === undefined
      ? dagDepthBoundary?.outgoingDepth || 0
      : hopLimit.downstream
  ).toString();

  const handleUpstreamHopChange = (val: string) => {
    if (val !== '') {
      onHopLimitChange({ ...hopLimit, upstream: Number(val) });
    }
  };

  const handleDownstreamHopChange = (val: string) => {
    if (val !== '') {
      onHopLimitChange({ ...hopLimit, downstream: Number(val) });
    }
  };

  const hasUpstreamAndDownstream =
    dagDepthBoundary?.outgoingDepth !== 0 &&
    dagDepthBoundary?.incomingDepth !== 0;

  return (
    <Flex sx={{ gap: 1, alignItems: 'center' }}>
      <HopSelector
        value={upstreamValue}
        onChange={handleUpstreamHopChange}
        maxValue={dagDepthBoundary?.incomingDepth || 0}
      />

      <Tooltip title="Upstream of selected nodes">
        <StreamButton
          $active={
            hopLimit?.downstream === 0 &&
            hopLimit?.upstream !== 0 &&
            hasUpstreamAndDownstream
          }
          disabled={!hasUpstreamAndDownstream}
          onClick={() =>
            onHopLimitChange({
              upstream: hopLimit?.upstream || dagDepthBoundary?.incomingDepth,
              downstream: 0,
            })
          }
        >
          <UpstreamIcon />
        </StreamButton>
      </Tooltip>

      <Tooltip title="Upstream & downstream of selected nodes">
        <StreamButton
          $active={
            (hopLimit?.upstream !== 0 && hopLimit?.downstream !== 0) ||
            (hopLimit?.upstream === 0 && hopLimit?.downstream === 0) ||
            !hasUpstreamAndDownstream
          }
          onClick={() =>
            onHopLimitChange({
              upstream: hopLimit?.upstream || dagDepthBoundary?.incomingDepth,
              downstream:
                hopLimit?.downstream || dagDepthBoundary?.outgoingDepth,
            })
          }
        >
          <StreamIcon />
        </StreamButton>
      </Tooltip>

      <Tooltip title="Downstream of selected nodes">
        <StreamButton
          $active={
            hopLimit?.upstream === 0 &&
            hopLimit?.downstream !== 0 &&
            hasUpstreamAndDownstream
          }
          disabled={!hasUpstreamAndDownstream}
          onClick={() =>
            onHopLimitChange({
              upstream: 0,
              downstream:
                hopLimit?.downstream || dagDepthBoundary?.outgoingDepth,
            })
          }
        >
          <DownstreamIcon />
        </StreamButton>
      </Tooltip>

      <HopSelector
        value={downstreamValue}
        onChange={handleDownstreamHopChange}
        maxValue={dagDepthBoundary?.outgoingDepth || 0}
      />
    </Flex>
  );
};

export default StreamSelector;
